import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdate } from 'react-use';
import { v4 as uuidv4 } from 'uuid';

import { accountsSelectors } from 'app-components/flux/accounts';
import { chartOfAccountsSelectors, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import { transactionsActions, transactionsTypes } from 'app-components/flux/transactions';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TextField from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';

import { tracker } from 'app-components/utils/core';
import AttachmentsField from 'components/AttachmentsField';
import Dump from 'components/Dump';
import PayeeAutocomplete from 'components/PayeeAutocomplete';
import AccountField from 'components/QuickenControls/AccountField';
import AmountField from 'components/QuickenControls/AmountField';
import CategoryField from 'components/QuickenControls/CategoryField';
import SDatePicker from 'components/SDatePicker';

const useStyles = makeStyles()((theme) => ({
  form: {
    position: 'relative',
  },
  amountField: {
    textAlign: 'left',
  },
  dropZonePlaceholder: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.default,
    opacity: 0.9,
    display: 'flex',
  },
  dropZoneContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    alignContent: 'center',
    border: `4px dashed ${theme.palette.border.main}`,
    borderRadius: 8,
    margin: 30,
  },
  statusImage: {
    width: 128,
    height: 128,
    marginTop: 24,
    marginBottom: 24,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));

const RefundForm = React.memo((props) => {
  const { transaction, onClose, onDirty, formikRef, ...otherProps } = props;

  const { classes } = useStyles();
  const dispatch = useDispatch();

  const dropzoneRef = useRef();
  const { onClick, onFocus, onBlur, onKeyDown, tabIndex, ...dropzoneRootProps } =
    dropzoneRef.current?.getRootProps?.() || {};
  const update = useUpdate();

  const accounts = useSelector(accountsSelectors.getRefundAccounts);

  const formik = useFormik({
    initialValues: {
      ...(transaction.id && transaction),
    },
    onSubmit: (values) => {
      const isTransfer = chartOfAccountsUtils.isTransferCOA(values.coa);
      const payload = [
        transactionsTypes.mkCashFlowTransaction({
          clientId: uuidv4(),
          ...values,
          amount: Number(values.amount),
          source: 'USER_ENTERED',
          state: 'PENDING',
          subtype: 'REFUND',
          isExcludedFromF2S: accounts?.toJS()?.[values.accountId]?.isExcludedFromBudgets || isTransfer || false,
          isExcludedFromReports: accounts?.toJS()?.[values.accountId]?.isExcludedFromReports || isTransfer || false,
          allocations: null,
        }),
      ];

      if (values.id) {
        dispatch(transactionsActions.batchTransactions(payload, { undo: { userMessage: 'Refund tracking updated.' } }));

        tracker.track(tracker.events.refundEdit, {
          category: chartOfAccountsSelectors.getCoaStringSelector(undefined, values.coa),
          fields_modified: [
            ...(formik.initialValues.payee !== values.payee ? ['payee'] : []),
            ...(formik.initialValues.amount != values.amount ? ['amount'] : []), // eslint-disable-line eqeqeq
            ...(formik.initialValues.postedOn !== values.postedOn ? ['date'] : []),
            ...(formik.initialValues.accountId !== values.accountId ? ['account'] : []),
            ...(!chartOfAccountsUtils.coasAreEqual(formik.initialValues.coa, values.coa) ? ['category'] : []),
            ...(formik.initialValues.memo !== values.memo ? ['notes'] : []),
            ...(formik.initialValues.attachments !== values.attachments ? ['attachments'] : []),
          ],
        });
      } else {
        dispatch(transactionsActions.batchTransactions(payload, { undo: { userMessage: 'Refund tracking created.' } }));

        tracker.track(tracker.events.refundCreate, {
          location: transaction?.subtypeRelatedTxn?.id ? 'transaction details' : 'refund tracker',
          category: chartOfAccountsSelectors.getCoaStringSelector(undefined, values.coa),
          fields_modified: [
            ...(formik.initialValues.payee !== values.payee ? ['payee'] : []),
            ...(formik.initialValues.amount != values.amount ? ['amount'] : []), // eslint-disable-line eqeqeq
            ...(formik.initialValues.postedOn !== values.postedOn ? ['date'] : []),
            ...(formik.initialValues.accountId !== values.accountId ? ['account'] : []),
            ...(!chartOfAccountsUtils.coasAreEqual(formik.initialValues.coa, values.coa) ? ['category'] : []),
            ...(formik.initialValues.memo !== values.memo ? ['notes'] : []),
            ...(formik.initialValues.attachments !== values.attachments ? ['attachments'] : []),
          ],
        });
      }
      onClose?.(undefined, 'submit');
    },
  });

  const formikSetValues = formik.setValues;
  useEffect(() => {
    formikSetValues({
      ...transaction,
      postedOn: transaction?.postedOn || DateTime.local().startOf('day').plus({ days: 3 }).toISODate(), // qcs match range [-5:+3] days
    });
  }, [formikSetValues, transaction]);

  useEffect(() => {
    formik.registerField('payee', { validate: (value) => (value ? undefined : 'please enter required value') });
    formik.registerField('amount', { validate: (value) => (value > 0 ? undefined : 'please use positive amount') });
    formik.registerField('postedOn', {
      validate: (value) => (DateTime.fromISO(value)?.isValid ? undefined : 'please use valid date'),
    });
    formik.registerField('accountId', { validate: (value) => (value ? undefined : 'please select an account') });
  }, [formik]);

  useEffect(() => {
    onDirty?.(formik.dirty);
  }, [formik.dirty, onDirty]);

  useImperativeHandle(formikRef, () => formik, [formik]);

  return (
    <form className={classes.form} onSubmit={formik.handleSubmit} {...otherProps} {...dropzoneRootProps}>
      <Dump obj={formik} />

      <PayeeAutocomplete
        id='payee'
        inputValue={formik.values.payee || ''}
        onInputChange={(event, value, _reason) => (event || value) && formik.setFieldValue('payee', value)}
        onBlur={formik.handleBlur}
        textFieldProps={{
          error: Boolean((formik.touched.payee || formik.submitCount) && formik.errors.payee),
          helperText: formik.touched.payee || formik.submitCount ? formik.errors.payee : undefined,
          // autoFocus
          variant: 'filled',
          margin: 'normal',
          fullWidth: true,
        }}
      />

      <AmountField
        id='amount'
        value={formik.values.amount}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={Boolean((formik.touched.amount || formik.submitCount) && formik.errors.amount)}
        helperText={formik.touched.amount || formik.submitCount ? formik.errors.amount : undefined}
        className={classes.amountField}
        showAmountAdornment
        editable
        omitCents={false}
        currencySymbol='USD'
        label='Amount'
        textFieldVariant='filled'
        marginProp='normal'
        autoComplete='one-time-code'
        fullWidth
      />

      <SDatePicker
        id='postedOn'
        label='Expected Date'
        value={formik.values.postedOn ? DateTime.fromISO(formik.values.postedOn) : null}
        onChange={(date, value) => formik.setFieldValue('postedOn', date?.isValid ? date?.toISODate?.() : value)}
        textFieldProps={{
          fullWidth: true,
          error: Boolean((formik.touched.postedOn || formik.submitCount) && formik.errors.postedOn),
          helperText:
            ((formik.touched.postedOn || formik.submitCount) && formik.errors.postedOn) ||
            'Let us know when to alert you if you haven’t received this',
        }}
        minDate={DateTime.local()}
      />

      <AccountField
        id='accountId'
        key={formik.values.accountId}
        accounts={accounts}
        initialAccountId={formik.values.accountId}
        onSelected={(account) => formik.setFieldValue('accountId', account?.id)}
        error={Boolean((formik.touched.accountId || formik.submitCount) && formik.errors.accountId)}
        helperText={formik.touched.accountId || formik.submitCount ? formik.errors.accountId : undefined}
        label='Account'
        margin='normal'
        variant='filled'
        autoComplete='one-time-code'
        fullWidth
      />

      <CategoryField
        id='coa'
        value={formik.values.coa}
        onChange={(coa) => formik.setFieldValue('coa', coa)}
        label='Category'
        editable
        createEnabled
        allowBlank
        margin='normal'
        textFieldVariant='filled'
        // textFieldProps={{
        //   error: formik.touched.coa && formik.errors.coa,
        //   helperText: formik.touched.coa && formik.errors.coa,
        // }}
      />

      <TextField
        id='memo'
        value={formik.values.memo || ''}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        label='Notes'
        margin='normal'
        variant='filled'
        placeholder='Type your notes here'
        fullWidth
      />

      {dropzoneRef.current?.isDragActive && (
        <div className={classes.dropZonePlaceholder}>
          <div className={classes.dropZoneContent}>
            <CloudUploadIcon className={classes.statusImage} />
            Drag & Drop to upload
          </div>
        </div>
      )}

      <AttachmentsField
        id='attachments'
        attachments={formik.values.attachments}
        onChange={(_event, attachments, _reason, _details) => formik.setFieldValue('attachments', attachments)}
        dropzoneRef={dropzoneRef}
        onDropzoneChanged={update}
      />
    </form>
  );
});

RefundForm.propTypes = {
  transaction: PropTypes.object,
  formikRef: PropTypes.object,
  onClose: PropTypes.func,
  onDirty: PropTypes.func,
};

RefundForm.whyDidYouRender = true;
RefundForm.displayName = 'RefundForm';
export default RefundForm;
